import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { canUseNextUi } from './next/uiFlag';
import { bootMode } from './boot';
import UnsupportedBrowser from './UnsupportedBrowser';
import { CrashScreen, LoadFailedScreen } from './bootScreens';
import nextCssHref from './styles/next.css?url';
import guestCssHref from './guest/guest.css?url';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  sendDefaultPii: false,
});

// What this load is - a guest QR page, a browser the interface cannot draw,
// or the app - decides which stylesheet loads at all. Rendering waits for the
// stylesheet so the first frame is never painted unstyled.
//
// `?url` (not a bare dynamic import()): Vite resolves import('./a.css') and
// import('./b.css') in a ternary to the same no-op module - the CSS itself
// gets linked by chunk analysis, not by which branch runs, so the runtime
// condition above would be ignored. `?url` gives plain hashed-URL strings,
// decoupled from that chunk-CSS association, so the <link> below is the only
// thing that decides.
const mode = bootMode({ search: window.location.search, canUseNextUi: canUseNextUi() });
const stylesheetReady = mode === 'unsupported'
  ? Promise.resolve()
  : new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    // Guest pages load guest.css, held to the guest browser floor: next.css is
    // Tailwind v4, which the old phones guests bring cannot draw.
    link.href = mode === 'app' ? nextCssHref : guestCssHref;
    link.onload = resolve;
    link.onerror = resolve;
    document.head.appendChild(link);
  });

// A chunk that fails to download (hotel Wi-Fi, mobile data) must not leave a
// blank page: the answer becomes LOAD_FAILED and a retry screen is drawn.
const LOAD_FAILED = Symbol("load-failed");
const rootReady = (mode === 'guest'
  ? import('./guest/GuestApp.jsx').then((m) => m.default)
  : mode === 'app'
    ? import('./AppRoot.jsx').then((m) => m.default)
    : Promise.resolve(null)
).catch(() => LOAD_FAILED);

Promise.all([stylesheetReady, rootReady]).then(([, Root]) => {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  if (mode === 'unsupported') {
    root.render(<UnsupportedBrowser />);
    return;
  }
  if (Root === LOAD_FAILED) {
    root.render(<LoadFailedScreen guest={mode === 'guest'} />);
    return;
  }
  root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={() => <CrashScreen guest={mode === 'guest'} />}>
      <Root />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
  );
});
